import { mapState } from 'vuex';

import { Errors } from '@/enums/errors.enum';
import { formMixin } from '@/mixins/form';
import { tryParseInt } from '@/utils/number';

import { methodsMixin } from './methods';
import { notificationsMixin } from './notifications';

/**
 * Mixin zarządzania formularzem edycji/dodawania
 *
 * @export
 * @param {string} moduleName - nazwa modułu
 * @param {Object} messages - treści notyfikacji
 * @param {string} messages.updated - wiadomość po aktualizacji encji
 * @param {string} messages.updateError - wiadomość w przypadku niepowodzenia przy aktualizacji encji
 * @param {string} messages.add - wiadomość po dodaniu encji
 * @param {string} messages.addError - wiadomość w przypadku niepowodzenia przy dodaniu encji
 * @param {string} messages.formError - treść notyfikacji o błędzie walidacji
 * @param {function} emptyModel - funkcja tworząca pusty model
 * @returns
 */
export function formViewMixin(moduleName, messages, emptyModel) {
  return {
    mixins: [formMixin, notificationsMixin, methodsMixin],
    data() {
      return {
        model: emptyModel(),
        id: this.edit ? this.$store.state[moduleName].id : undefined
      };
    },
    props: {
      // Czy pobierać dane do formularza
      edit: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    methods: {
      // @vuese
      // Funkcja waliduje formularz, i zapisuje/nadpisuje dane w bazie
      save() {
        if (!this.$schema.$validateAll().length) {
          if (this.edit) {
            // Emituje event do edytowania danych
            // @arg Zwalidowany model do edycji w bazie
            this.update({
              id: tryParseInt(this.id),
              ...this.prepareModel(this.model)
            });
          } else {
            // Emituje event do zapisu danych
            // @arg Zwalidowany model do utworzenia w bazie
            this.add(this.model);
          }
        } else {
          this.notifyError(messages.formError || Errors.FORM_INCORRECTLY_COMPLETED);
        }
      },
      // @vuese
      // Zamyka widok formularza
      cancel() {
        // Zamyka widok formularza
        this.$emit('cancel');
      },

      // @vuese
      // Ustawia dane formularza
      setData() {
        this.$schema.$setData(this.edit ? this.stateData : emptyModel());
        this.$schema.$reset();
      },

      // @vuese
      // Zapisuje encję
      add(data) {
        this.$store
          .dispatch(`${moduleName}/add`, this.deepClone(data))
          .then(id => {
            this.notifySuccess(messages.added);
            this.setData();
            this.afterSave({ id, ...data });
          })
          .catch(err => {
            err.message ? this.notifyError(messages.addError, [err.message]) : this.notifyError(messages.addError);
          });
      },
      // @vuese
      // Aktualizuje encję
      update(data) {
        this.$store
          .dispatch(`${moduleName}/update`, this.deepClone(data))
          .then(() => {
            this.notifySuccess(messages.updated);
            this.afterSave(data);
          })
          .catch(err => {
            err.message ? this.notifyError(messages.updateError, [err.message]) : this.notifyError(messages.addError);
          });
      },
      // @vuese
      // Funkcja mapująca model przed zapisem
      prepareModel(model) {
        return model;
      },
      // @vuese
      // Funkcja wykonywana po zapisie
      afterSave(data) {},
      // @vuese
      // Obsługa zapisu przez CTRL + S
      handleCtrlSClick(event) {
        if ((event.ctrlKey || event.metaKey) && event.key === 's') {
          event.preventDefault();
          this.save();
        }
      }
    },
    computed: mapState({
      stateData: state => state[moduleName].data
    }),
    mounted() {
      this.setData();
      window.onkeydown = event => this.handleCtrlSClick(event);
    },
    beforeDestroy() {
      window.onkeydown = null;
    }
  };
}
